import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { adminApi, formatApiError } from "../../lib/api";
import { useEvents } from "../../lib/events";
import { useDialog } from "../../lib/dialog";
import { useSettings } from "../../lib/settings";
import AdminLayout from "../../components/app/AdminLayout";
import { Card } from "./Dashboard";
import { toast } from "sonner";
import { Plus, X, Calendar, Users, ArrowRight, Archive, Lock, Unlock, RotateCcw, Copy, Trash2 } from "lucide-react";

const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const STATUS_META = {
  active: { label: "Active", color: "#10B981" },
  closed: { label: "Applications Closed", color: "#F59E0B" },
  archived: { label: "Archived", color: "#6B6862" },
};

export default function Events() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const { formatDate } = useSettings();
  const { events, currentId, setCurrentEvent, reloadEvents, loaded } = useEvents();
  const [showForm, setShowForm] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => { reloadEvents(); }, [reloadEvents]);
  useEffect(() => {
    if (params.get("new")) {
      setShowForm(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const manage = (ev) => { setCurrentEvent(ev.id); navigate("/admin"); };

  const changeStatus = async (ev, status, confirmCopy) => {
    if (confirmCopy && !(await dialog.confirm(confirmCopy))) return;
    await adminApi.patch(`/admin/events/${ev.id}`, { status });
    await reloadEvents();
    toast.success(`Event set to ${STATUS_META[status].label}`);
  };

  const removeEvent = async (ev) => {
    if (events.length <= 1) {
      await dialog.alert({ title: "Cannot delete the only event", description: "Create another event first — the system always needs at least one event." });
      return;
    }
    // Step 1 — warn about permanent cascade deletion
    const proceed = await dialog.confirm({
      title: "Permanently delete this event?",
      description: `ALL data for "${ev.name}" will be erased forever — every applicant, role, show date, roster, sign-in, payroll record, audition, the application form and email templates. This cannot be undone.`,
      confirmLabel: "Continue",
      destructive: true,
    });
    if (!proceed) return;
    // Step 2 — type the event name to confirm
    const typed = await dialog.prompt({
      title: "Final confirmation",
      description: `This is your last chance. Type the event name exactly to permanently delete it:  ${ev.name}`,
      placeholder: ev.name,
      confirmLabel: "Delete Forever",
      destructive: true,
    });
    if (typed === null) return;
    if (typed.trim() !== ev.name.trim()) {
      toast.error("Name did not match — deletion cancelled.");
      return;
    }
    try {
      await adminApi.delete(`/admin/events/${ev.id}`);
      const remaining = await reloadEvents();
      if (currentId === ev.id) {
        const next = remaining.find((e) => e.status === "active") || remaining[0];
        if (next) setCurrentEvent(next.id);
      }
      toast.success(`"${ev.name}" and all its data were permanently deleted.`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const active = events.filter((e) => e.status !== "archived");
  const archived = events.filter((e) => e.status === "archived");

  return (
    <AdminLayout title="Events" subtitle="Manage your seasons and productions"
      actions={<button onClick={() => setShowForm((v) => !v)} data-testid="new-event-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]"><Plus size={14} /> Create New Event</button>}>

      {showForm && <CreateEventForm events={events} onClose={() => setShowForm(false)} onCreated={async (ev) => { await reloadEvents(); setCurrentEvent(ev.id); setShowForm(false); toast.success("Event created"); }} />}

      {!loaded ? (
        <p className="text-sm text-[#6B6862]">Loading events…</p>
      ) : events.length === 0 ? (
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-12 text-center">
          <p className="text-[#A19D94]">No events yet. Create your first event to get started.</p>
        </div>
      ) : (
        <div className="space-y-8">
          <div>
            <h2 className="label-mini mb-3">Active Events</h2>
            {active.length === 0 ? <p className="text-sm text-[#6B6862]">No active events.</p> : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {active.map((ev) => <EventCard key={ev.id} ev={ev} current={ev.id === currentId} onManage={manage} onStatus={changeStatus} onDelete={removeEvent} formatDate={formatDate} />)}
              </div>
            )}
          </div>
          {archived.length > 0 && (
            <div>
              <h2 className="label-mini mb-3 flex items-center gap-2"><Archive size={13} /> Archived Events</h2>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {archived.map((ev) => <EventCard key={ev.id} ev={ev} current={ev.id === currentId} onManage={manage} onStatus={changeStatus} onDelete={removeEvent} formatDate={formatDate} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

function EventCard({ ev, current, onManage, onStatus, onDelete, formatDate }) {
  const meta = STATUS_META[ev.status] || STATUS_META.active;
  return (
    <div data-testid={`event-card-${ev.id}`} className={`border bg-[#0A0A0A] p-5 flex flex-col ${current ? "border-blood" : "border-[#1a1a1a]"}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display text-xl uppercase tracking-tight leading-tight">{ev.name}</h3>
        <span className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase px-2 py-1 border" style={{ borderColor: meta.color + "80", color: meta.color }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />{meta.label}
        </span>
      </div>
      <div className="font-mono text-[11px] text-[#6B6862] mt-1">/apply/{ev.slug}</div>

      <div className="mt-4 space-y-1.5 text-xs text-[#A19D94]">
        <div className="flex items-center gap-2"><Users size={13} className="text-blood" /> {ev.applicant_count} applicant{ev.applicant_count === 1 ? "" : "s"}</div>
        <div className="flex items-center gap-2"><Calendar size={13} className="text-blood" /> Opens: {ev.applications_open_at ? formatDate(ev.applications_open_at) : "Immediately"}</div>
        <div className="flex items-center gap-2"><Calendar size={13} className="text-blood" /> Closes: {ev.applications_close_at ? formatDate(ev.applications_close_at) : "Manually"}</div>
      </div>

      <div className="mt-5 pt-4 border-t border-[#1a1a1a] flex flex-wrap items-center gap-2">
        <button onClick={() => onManage(ev)} data-testid={`event-manage-${ev.id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">Manage <ArrowRight size={12} /></button>
        {ev.status === "active" && (
          <button onClick={() => onStatus(ev, "closed")} data-testid={`event-close-${ev.id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood"><Lock size={12} /> Close Apps</button>
        )}
        {ev.status === "closed" && (
          <button onClick={() => onStatus(ev, "active")} data-testid={`event-reopen-${ev.id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#10B981] hover:text-[#10B981]"><Unlock size={12} /> Reopen</button>
        )}
        {ev.status !== "archived" ? (
          <button onClick={() => onStatus(ev, "archived", { title: "Archive this event?", description: "Archived events become read-only — you can view all data but cannot make changes. You can restore it later if needed.", confirmLabel: "Archive", destructive: true })} data-testid={`event-archive-${ev.id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#6B6862] hover:border-[#A19D94] hover:text-[#A19D94]"><Archive size={12} /> Archive</button>
        ) : (
          <button onClick={() => onStatus(ev, "active", { title: "Restore this event?", description: "This event will become active and editable again.", confirmLabel: "Restore" })} data-testid={`event-restore-${ev.id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#10B981] hover:text-[#10B981]"><RotateCcw size={12} /> Restore</button>
        )}
        <button onClick={() => onDelete(ev)} data-testid={`event-delete-${ev.id}`} title="Delete event permanently" className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider text-[#6B6862] hover:text-blood transition-colors"><Trash2 size={12} /> Delete</button>
      </div>
    </div>
  );
}

function CreateEventForm({ events, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [openDate, setOpenDate] = useState("");
  const [closeDate, setCloseDate] = useState("");
  const [copyFrom, setCopyFrom] = useState("");
  const [copy, setCopy] = useState({ form: false, roles: false, templates: false });
  const [saving, setSaving] = useState(false);

  const effSlug = slugEdited ? slug : slugify(name);
  const setAll = (v) => setCopy({ form: v, roles: v, templates: v });
  const allChecked = copy.form && copy.roles && copy.templates;

  const create = async () => {
    if (!name.trim()) { toast.error("Event name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        slug: effSlug || undefined,
        applications_open_at: openDate ? `${openDate}T00:00:00` : null,
        applications_close_at: closeDate ? `${closeDate}T23:59:59` : null,
        copy_from_event_id: copyFrom || null,
        copy_form: !!copyFrom && copy.form,
        copy_roles: !!copyFrom && copy.roles,
        copy_templates: !!copyFrom && copy.templates,
      };
      const { data } = await adminApi.post("/admin/events", payload);
      onCreated(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card title="Create New Event" className="mb-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="label-mini block mb-1.5">Event Name *</label>
          <input data-testid="event-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Stourbridge Scare Maze 2027"
            className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
        </div>
        <div>
          <label className="label-mini block mb-1.5">Public URL Slug</label>
          <div className="flex items-center bg-[#050505] border border-[#262626] focus-within:border-blood">
            <span className="px-3 text-xs text-[#6B6862] font-mono border-r border-[#262626] py-2.5">/apply/</span>
            <input data-testid="event-slug-input" value={effSlug} onChange={(e) => { setSlugEdited(true); setSlug(slugify(e.target.value)); }}
              className="flex-1 bg-transparent px-3 py-2.5 text-sm font-mono focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="label-mini block mb-1.5">Applications Open Date</label>
          <input data-testid="event-open-date" type="date" value={openDate} onChange={(e) => setOpenDate(e.target.value)}
            className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
          <p className="text-[10px] text-[#6B6862] mt-1">Leave blank to open immediately.</p>
        </div>
        <div>
          <label className="label-mini block mb-1.5">Applications Close Date</label>
          <input data-testid="event-close-date" type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)}
            className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
          <p className="text-[10px] text-[#6B6862] mt-1">Leave blank to close manually.</p>
        </div>
      </div>

      {events.length > 0 && (
        <div className="mt-5 pt-5 border-t border-[#1a1a1a]">
          <label className="label-mini block mb-1.5 flex items-center gap-1.5"><Copy size={12} /> Copy settings from a previous event?</label>
          <select data-testid="event-copy-from" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}
            className="w-full md:w-1/2 bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood">
            <option value="">— Start fresh (no copy) —</option>
            {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          {copyFrom && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <CopyToggle label="All of the below" checked={allChecked} onChange={(v) => setAll(v)} testid="copy-all" />
              <CopyToggle label="Application form" checked={copy.form} onChange={(v) => setCopy((c) => ({ ...c, form: v }))} testid="copy-form" />
              <CopyToggle label="Roles list" checked={copy.roles} onChange={(v) => setCopy((c) => ({ ...c, roles: v }))} testid="copy-roles" />
              <CopyToggle label="Email templates" checked={copy.templates} onChange={(v) => setCopy((c) => ({ ...c, templates: v }))} testid="copy-templates" />
            </div>
          )}
        </div>
      )}

      <div className="mt-5 flex gap-2">
        <button onClick={create} disabled={saving} data-testid="event-create-submit" className="px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40">{saving ? "Creating…" : "Create Event"}</button>
        <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#3a3835] flex items-center gap-1.5"><X size={13} /> Cancel</button>
      </div>
    </Card>
  );
}

const CopyToggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#D91616]" />
    <span className="text-[#A19D94]">{label}</span>
  </label>
);
